/**
 * StrategyOrchestrator
 *
 * High-level orchestration service that coordinates the complete strategy lifecycle:
 * - createWithAIServer: Creates strategy -> hydrates data -> starts Builder Service AI task
 *
 * HB-6 (D-L2-5): Path B — `regenerateBlock` / `regenerateBlockWithServer` and the
 * client-side hydration behind them — is DELETED. Every block update goes through
 * `AgentApiClient.updateBlock` (developer rule 25).
 *
 * This service implements FR-008 (AI integration) and FR-009 (save strategy even if AI fails).
 *
 * Related: Sprint 7, FR-008, FR-009, FR-052, FR-056
 */

import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { SourceDataHydrator, type SourceData, type HydratedSourceData } from './SourceDataHydrator';
import { cleanSourceDataForAI } from '../../builder-service/src/agent/utils/aiInputCleaners';
import type { Strategy } from '@segmently/schema-models/strategy';
import {
  normalizeBlockGenerationModeRequest,
  type StrategyBlockGenerationMode,
} from '@modules/shared-types';

// Content Agent imports for server-side model-routed generation
import {
  AgentApiClient,
  type BlockType,
} from '@modules/content-agent';

/**
 * ServiceFactory interface for creating strategy-related services
 *
 * This factory pattern enables proper dependency injection and testability
 * by allowing external control over service instantiation.
 */
export interface ServiceFactory {
  createBlockService(strategyId: string): StrategyBlockService;
}

/**
 * Default implementation of ServiceFactory for production use
 */
class DefaultServiceFactory implements ServiceFactory {
  constructor(private readonly projectId: string) {}

  createBlockService(strategyId: string): StrategyBlockService {
    return new StrategyBlockService(this.projectId, strategyId);
  }
}

/**
 * Input for creating a strategy
 */
export interface CreateStrategyInput {
  name: string;
  description?: string;
  strategyType?: Strategy['strategyType'];
  style?: string;
  toneOfVoice?: string;
  designDescription?: string;
  sourceData?: Record<string, unknown>; // Source data references (audiences, tasks, studyCases) - legacy format
  sourceDataMapping?: Array<{ sourcePath: string }>; // User-selected paths for UI restoration - new format
  /**
   * SG-A: the strategy-owned block dispatch mode. It is a STRATEGY setting, not a
   * per-run option, so it travels in this packet and is written in the SAME
   * document write as the rest of the head. Omitted = the field is not written
   * at all (absent resolves to `parallel`, keeping pre-SG-A creations
   * byte-identical). An invalid value throws before any write.
   */
  blockGenerationMode?: StrategyBlockGenerationMode;
}

/**
 * Result of createWithAI operation
 */
export interface CreateWithAIResult {
  success: boolean;
  /**
   * F-LIVE2-40: absent when the create was COMPENSATED — the server refused the
   * generate request synchronously (4xx) and the document created seconds
   * earlier was deleted again, so there is no strategy to hand back.
   */
  strategy?: Strategy;
  error?: string;
  /**
   * F-LIVE2-40: the server refused the request with a 4xx validation answer and
   * nothing was created. The caller must NOT report «created, but…» — nothing
   * exists. Never set for a 5xx / network / timeout failure: there the document
   * stays and the operator can regenerate.
   */
  refused?: boolean;
}

/**
 * D-LIVE-3: a strategy head's `sourceData.<collection>` entries can be either
 * plain id strings (legacy shape) or hydrated objects (the shape the create
 * wizard writes — `{ id, name, description, … }`). Accept either: a string id
 * stays as-is, an object yields `id ?? entityId`, and entries that resolve to
 * nothing (no id, no entityId) are dropped rather than forwarded as ids.
 */
function extractSourceDataIds(entries: unknown): string[] {
  if (!Array.isArray(entries)) {
    return [];
  }
  return entries
    .map((entry) => (typeof entry === 'string' ? entry : entry?.id ?? entry?.entityId))
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/**
 * StrategyOrchestrator Service
 *
 * Coordinates multiple services to implement complete AI-powered strategy workflows.
 * Uses dependency injection for all service dependencies.
 *
 * Usage:
 * ```typescript
 * const orchestrator = new StrategyOrchestrator(
 *   projectId,
 *   strategyService,
 *   hydrator
 * );
 *
 * const result = await orchestrator.createWithAIServer(
 *   { name: 'My Strategy', description: 'Test' },
 *   { audienceIds: ['aud-1'], taskIds: [], studyCaseIds: [] }
 * );
 * ```
 */
export class StrategyOrchestrator {
  private readonly serviceFactory: ServiceFactory;

  constructor(
    private readonly projectId: string,
    private readonly strategyService: StrategyService,
    private readonly hydrator: SourceDataHydrator,
    serviceFactory?: ServiceFactory
  ) {
    // Use provided factory or create default one
    this.serviceFactory = serviceFactory || new DefaultServiceFactory(projectId);
  }

  /**
   * Create strategy with AI using server-side multi-phase pipeline.
   *
   * Fire-and-forget pattern: creates strategy in Firestore, sends request to
   * builder-service /api/agent/generate-strategy, returns immediately.
   * Server executes Phase 0-4 pipeline and saves blocks/screens to Firestore.
   * Frontend tracks progress via real-time Firestore listener on task document.
   *
   * @param strategyData - Strategy metadata (name, description, etc.)
   * @param sourceDataIds - IDs of source data to hydrate
   * @param options - Optional: provider, model, maxTokens, evalMaxIterations
   * @returns {Promise<CreateWithAIResult>} Result with strategy and taskId
   */
  async createWithAIServer(
    strategyData: CreateStrategyInput,
    sourceDataIds: SourceData,
    options?: {
      provider?: string;
      model?: string;
      maxTokens?: number;
      evalMaxIterations?: number;
      evalEnabled?: boolean;
      generateScreens?: boolean;
      /** Phase V (D7/D28): snapshot pin + fork lineage, recorded in inputManifest. */
      insightsAnalysisId?: string;
      /**
       * W13 (IS1): WHICH theses of the pinned analysis ground this run, and
       * whether the actors' quotes travel. Omitted = every thesis (today's
       * top-N projection) with quotes off — the baseline request stays
       * byte-identical (AC-W13-34).
       */
      insightSelection?: import('@modules/shared-types').AgentInsightSelection;
      /** Strategy variable package resolution: accepted insight-variable generation to merge with project variables. */
      insightVariableGenerationId?: string;
      /** Optional subset of resolved variables to include. */
      variableIds?: string[];
      /**
       * Task 9 (AC-019): the accepted insight-derived variable ids the operator
       * ticked. Omitted = every accepted variable of the generation.
       */
      insightVariableIds?: string[];
      /** Include project variableSet variables as baseline; backend defaults to true. */
      useProjectVariables?: boolean;
      predecessorStrategyId?: string;
      /** Exact immutable predecessor version for successor/fork lineage. */
      predecessorVersion?: string;
      forkHypothesis?: string;
      /** UXR C7: operator whitelist of block types + subtype slugs. Omit = unrestricted. */
      availableBlocks?: { blockTypes: string[]; subtypeSlugs: string[] };
      /** Block Examples Library (Phase 3): selected building blocks. Omit = byte-identical baseline. */
      libraryBlockSelections?: import('@modules/shared-types').BlockLibrarySelection[];
    }
  ): Promise<CreateWithAIResult & { taskId?: string }> {
    try {
      // Step 1: Hydrate source data
      console.log('[StrategyOrchestrator] [Server] Hydrating source data...');
      const hydratedData = await this.hydrator.hydrate(sourceDataIds);

      // Step 2: Create strategy document (MUST succeed before AI call - FR-009)
      // SG-A: the mode is validated HERE, before the write, so a rejected value
      // fails creation loudly instead of persisting a different mode (AC-SG-12);
      // an omitted mode never reaches the packet as an `undefined` key.
      const { blockGenerationMode: requestedBlockGenerationMode, ...headData } = strategyData;
      const blockGenerationMode = normalizeBlockGenerationModeRequest(requestedBlockGenerationMode);

      console.log('[StrategyOrchestrator] [Server] Creating strategy document...');
      const strategy = await this.strategyService.create({
        ...headData,
        ...(blockGenerationMode ? { blockGenerationMode } : {}),
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      if (!strategy.id) {
        throw new Error('Strategy creation failed - no ID returned');
      }

      console.log('[StrategyOrchestrator] [Server] Strategy created:', strategy.id);

      // Step 3: Ensure the strategy owns a theme snapshot before the backend
      // task can merge AI simplified screens into full V2 templates.
      await this.strategyService.ensureThemeSettingsV2FromActiveProjectTheme(strategy.id, {
        required: Boolean(options?.generateScreens),
      });

      // Step 4: Get user ID and strategy variables
      const coreService = CoreFirebaseService.getInstance();
      const userId = coreService.getCurrentUserId() || 'unknown';

      // Variables: from strategy (just cloned from project) or from project directly
      const strategyVariables = (strategy as any)?.variables ?? {};

      // Step 5: Fire-and-forget — call server-side pipeline
      console.log('[StrategyOrchestrator] [Server] Sending to generate-strategy endpoint...');
      const agentClient = new AgentApiClient();

      const response = await agentClient.generateStrategy({
        projectId: this.projectId,
        strategyId: strategy.id,
        userId,
        name: strategyData.name,
        description: strategyData.description,
        strategyType: strategyData.strategyType || 'IOS Onboarding',
        toneOfVoice: strategyData.toneOfVoice,
        designDescription: strategyData.designDescription,
        sourceData: cleanSourceDataForAI(hydratedData) as any,
        strategyVariables,
        provider: options?.provider as any,
        model: options?.model,
        maxTokens: options?.maxTokens,
        evalMaxIterations: options?.evalMaxIterations,
        evalEnabled: options?.evalEnabled,
        // F-LIVE2-22: an OMITTED screens choice must stay omitted — the server
        // then inherits a fork's predecessor shape instead of being told `false`.
        ...(options?.generateScreens === undefined
          ? {}
          : { generateScreens: options.generateScreens }),
        insightsAnalysisId: options?.insightsAnalysisId,
        ...(options?.insightSelection ? { insightSelection: options.insightSelection } : {}),
        insightVariableGenerationId: options?.insightVariableGenerationId,
        variableIds: options?.variableIds,
        insightVariableIds: options?.insightVariableIds,
        useProjectVariables: options?.useProjectVariables,
        predecessorStrategyId: options?.predecessorStrategyId,
        predecessorVersion: options?.predecessorVersion,
        forkHypothesis: options?.forkHypothesis,
        availableBlocks: options?.availableBlocks,
        // Block Examples Library (Phase 3): only when selections exist —
        // baseline requests stay byte-identical.
        ...(options?.libraryBlockSelections?.length
          ? { libraryBlockSelections: options.libraryBlockSelections }
          : {}),
      });

      if (!response.success) {
        console.warn('[StrategyOrchestrator] [Server] Generate request failed:', response.error);
        /**
         * F-LIVE2-40 — COMPENSATION, the inverse of the create above.
         *
         * FR-009 («the document MUST exist before the AI call») still holds: the
         * write happens first, unconditionally. What changes is the failure that
         * is CERTAIN — a synchronous 4xx validation answer means the server
         * started nothing (no task, no spend, no blocks), so the document this
         * method created seconds earlier is a pure orphan and is deleted again
         * through its owning service, the same way `CloneBlockAdvancedCommand`
         * compensates a half-applied clone.
         *
         * Any OTHER failure keeps the document: a 5xx, a network error or a
         * timeout leaves the server's state unknown, and deleting a strategy a
         * task may already be writing into would destroy work. There the
         * operator keeps the strategy and can regenerate.
         *
         * Ruling FW9-R4 — why the WHOLE synchronous 4xx range, not just the 400
         * shape: every 4xx the generate endpoint returns refuses before any task
         * exists (the generation lock writes the task document inside the very
         * transaction that claims it), so deleting the just-created, task-less
         * document is always the exact inverse of the create; the inline
         * sentence the dialog shows names the server's own reason.
         */
        const { httpStatus } = response;
        const refusedSynchronously = typeof httpStatus === 'number'
          && httpStatus >= 400 && httpStatus < 500;
        if (refusedSynchronously) {
          try {
            await this.strategyService.delete(strategy.id);
            console.warn(
              '[StrategyOrchestrator] [Server] Compensated a refused generation: strategy deleted',
              strategy.id,
            );
            return { success: false, strategy: undefined, error: response.error, refused: true };
          } catch (compensationError) {
            // Best-effort, like every compensation: report the ORIGINAL refusal
            // and hand the document back, because it is still there.
            console.error(
              '[StrategyOrchestrator] [Server] Compensation failed; the strategy may be orphaned:',
              compensationError,
            );
          }
        }
        return {
          success: false,
          strategy,
          error: response.error,
        };
      }

      console.log('[StrategyOrchestrator] [Server] ✅ Task created:', response.taskId);

      // Return immediately — server processes in background
      // Real-time Firestore listeners will update UI as blocks are created
      return {
        success: true,
        strategy,
        taskId: response.taskId,
      };
    } catch (error) {
      console.error('[StrategyOrchestrator] [Server] Error in createWithAIServer:', error);
      throw error;
    }
  }

  /**
   * UXR C4: launch generation for an ALREADY SAVED strategy (no create).
   *
   * Re-supplies the route-required fields (name, strategyType, sourceData)
   * from the persisted strategy document and reuses its strategyId — the
   * existing POST /agent/generate-strategy replaces blocks/screens in place.
   * A failed START is non-destructive: the route errors before any write.
   */
  async generateForExistingStrategy(
    strategy: Strategy & { id: string },
    options?: {
      model?: string;
      maxTokens?: number;
      generateScreens?: boolean;
      availableBlocks?: { blockTypes: string[]; subtypeSlugs: string[] };
      /**
       * W13 (IS1): the thesis selection the operator has ticked NOW. A request
       * that carries one OVERRIDES the selection frozen on the strategy; one
       * that omits it lets the server reuse the frozen value (the canvas
       * «generate again» path, which asks no selection question).
       */
      insightSelection?: import('@modules/shared-types').AgentInsightSelection;
    },
  ): Promise<{ success: boolean; taskId?: string; error?: string }> {
    try {
      const saved = strategy as Strategy & { id: string } & Record<string, any>;
      const sourceData = (saved.sourceData ?? {}) as Record<string, any>;
      const sourceDataIds: SourceData = {
        audienceIds: extractSourceDataIds(sourceData.audiences),
        taskIds: extractSourceDataIds(sourceData.tasks),
        studyCaseIds: extractSourceDataIds(sourceData.studyCases),
        reviewIds: extractSourceDataIds(sourceData.reviews),
        ratingIds: extractSourceDataIds(sourceData.ratings),
        bigNumberIds: extractSourceDataIds(sourceData.bigNumbers),
        insightIds: sourceData.insightIds ?? [],
      };

      console.log('[StrategyOrchestrator] [Server] Hydrating saved-strategy source data...');
      const hydratedData = await this.hydrator.hydrate(sourceDataIds);

      // Screens need a theme snapshot; required only when screens are requested.
      await this.strategyService.ensureThemeSettingsV2FromActiveProjectTheme(saved.id, {
        required: Boolean(options?.generateScreens),
      });

      const coreService = CoreFirebaseService.getInstance();
      const userId = coreService.getCurrentUserId() || 'unknown';
      const agentClient = new AgentApiClient();

      const response = await agentClient.generateStrategy({
        projectId: this.projectId,
        strategyId: saved.id,
        userId,
        name: saved.name ?? 'Strategy',
        description: saved.description,
        strategyType: saved.strategyType || 'IOS Onboarding',
        toneOfVoice: saved.toneOfVoice,
        designDescription: saved.designDescription,
        sourceData: cleanSourceDataForAI(hydratedData) as any,
        strategyVariables: saved.variables ?? {},
        model: options?.model,
        maxTokens: options?.maxTokens,
        generateScreens: options?.generateScreens,
        // Ground on what the strategy was pinned to at creation time.
        insightsAnalysisId: saved.inputManifest?.insightsAnalysisId ?? sourceDataIds.insightIds?.[0],
        ...(options?.insightSelection ? { insightSelection: options.insightSelection } : {}),
        useProjectVariables: true,
        availableBlocks: options?.availableBlocks,
      });

      if (!response.success) {
        console.warn('[StrategyOrchestrator] [Server] Generate-for-saved request failed:', response.error);
        return { success: false, error: response.error };
      }
      console.log('[StrategyOrchestrator] [Server] ✅ Generate-for-saved task created:', response.taskId);
      return { success: true, taskId: response.taskId };
    } catch (error) {
      console.error('[StrategyOrchestrator] [Server] Error in generateForExistingStrategy:', error);
      return { success: false, error: (error as Error)?.message || 'Unknown error' };
    }
  }

  /**
   * HB-6 (D-L2-5, 2026-09-14): `regenerateBlock`, `regenerateBlockWithServer`,
   * `generateBlockScreens` and `buildBlockPayload` are DELETED. They were Path B
   * — a client-side hydration of picker-selected ids sent as request
   * `sourceData`, which the server discards on every snapshot strategy
   * (`ignoredRequestReason`). Block screen generation has ONE path: the
   * block-update dialog's `AgentApiClient.updateBlock` request, resolved
   * server-side from the strategy snapshot. Block JSON export goes through
   * `StrategyExportService.exportBlockPayload`, which builds the same request
   * from the strategy's own source data.
   */

  /**
   * Build the exact same payload that createWithAI sends to the server.
   * Used for JSON export — guarantees identical data to what AI receives.
   */
  async buildStrategyPayload(
    sourceDataIds: SourceData,
    strategyId?: string,
  ): Promise<Record<string, any>> {
    const hydratedData = await this.hydrator.hydrate(sourceDataIds);

    const coreService = CoreFirebaseService.getInstance();
    const userId = coreService.getCurrentUserId() || 'export';

    // Load strategy if exists
    let strategy: any = null;
    if (strategyId) {
      strategy = await this.strategyService.get(strategyId);
    }

    return {
      projectId: this.projectId,
      strategyId: strategyId || 'new',
      userId,
      name: strategy?.name || 'Strategy',
      description: strategy?.description,
      strategyType: strategy?.strategyType || 'IOS Onboarding',
      toneOfVoice: strategy?.toneOfVoice,
      designDescription: strategy?.designDescription,
      sourceData: cleanSourceDataForAI(hydratedData),
      strategyVariables: strategy?.variables,
    };
  }

}
